import { Button } from "../../components/ui";
import React, { useState, useRef, useEffect } from 'react';

export interface Column {
  key: string;
  label: string;
  render?: (value: any, item: any, index: number) => React.ReactNode;
  textAlign?: 'left' | 'center' | 'right';
  width?: string;
}

interface StakeTableProps {
  title: string;
  columns: Column[];
  data: any[];
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  actions?: React.ReactNode;
  bottomActions?: React.ReactNode;
  page?: number;
  totalPages?: number;
  total?: number;
  limit?: number;
  onLimitChange?: (limit: number) => void;
  onPageChange?: (page: number) => void;
  emptyMessage?: string;
  hasSidebarMargin?: boolean;
  isLoading?: boolean;
  selectable?: boolean;
  selectedIds?: any[];
  onSelectionChange?: (ids: any[]) => void;
  onRefresh?: () => void;
  inlineBottomActions?: boolean;
}

const StakeTable: React.FC<StakeTableProps> = ({
  title,
  columns,
  data,
  searchPlaceholder = "Search...",
  onSearch,
  actions,
  bottomActions,
  page = 1,
  totalPages = 0,
  total = 0,
  limit = 50,
  onLimitChange,
  onPageChange,
  emptyMessage = "No data found.",
  hasSidebarMargin = true,
  isLoading = false,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onRefresh,
  inlineBottomActions = false
}) => {
  const [showColumnToggle, setShowColumnToggle] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowColumnToggle(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleColumn = (key: string) => {
    setHiddenColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const visibleColumnsData = columns.filter(col => !hiddenColumns.includes(col.key));

  const handleRefresh = async () => {
    setIsRefreshing(true);
    if (onRefresh) onRefresh();
    else window.location.reload();
    setTimeout(() => setIsRefreshing(false), 800);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange) return;
    if (e.target.checked) {
      onSelectionChange(data.map((item) => item.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (id: any, checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter((selId) => selId !== id));
    }
  };

  const allSelected = data.length > 0 && selectedIds.length === data.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < data.length;

  const startRecord = total > 0 ? (page - 1) * limit + 1 : 0;
  const endRecord = Math.min(page * limit, total);

  return (
    <div className={`${hasSidebarMargin ? 'ml-0 lg:ml-[256px] p-4 sm:p-6' : ''}`}>
      
      {/* ── Toolbar ────────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3.5 mb-4">

        {/* Row 1: Title (or Search if title empty) + Icon Buttons + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
          {title ? (
            <h1 className="text-base sm:text-xl font-bold text-white tracking-tight shrink-0">
              {title}
            </h1>
          ) : onSearch ? (
            <div className="relative w-full sm:max-w-xs">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={searchPlaceholder}
                onChange={(e) => onSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-all"
              />
            </div>
          ) : (
            <div className="hidden sm:block" />
          )}

          <div className="flex flex-wrap items-center gap-2 justify-end w-full sm:w-auto">
            {/* Actions (Export, Filters, Create etc) */}
            {actions && <div className="w-full sm:w-auto">{actions}</div>}

            {/* Refresh */}
            <button
              onClick={handleRefresh}
              title="Refresh"
              className={`w-9 h-9 flex items-center justify-center rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all shrink-0 ${isRefreshing ? 'animate-spin' : ''}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>

            {/* Columns Toggle */}
            <div className="relative shrink-0" ref={dropdownRef}>
              <button
                onClick={() => setShowColumnToggle(!showColumnToggle)}
                title="Toggle Columns"
                className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
              </button>
              {showColumnToggle && (
                <div className="absolute right-[-120px] sm:right-0 mt-2 w-52 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-slate-700 bg-slate-900/60">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Columns</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-2 flex flex-col gap-0.5">
                    {columns.map(col => (
                      <label
                        key={`toggle-${col.key}`}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-slate-700/50 rounded-lg cursor-pointer transition-colors"
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${!hiddenColumns.includes(col.key) ? 'bg-indigo-600 border-indigo-600' : 'bg-transparent border-slate-600'}`}>
                          {!hiddenColumns.includes(col.key) && (
                            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 12 12">
                              <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                            </svg>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={!hiddenColumns.includes(col.key)}
                          onChange={() => toggleColumn(col.key)}
                          className="sr-only"
                        />
                        <span className="text-sm text-slate-200">{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Search & Filters Row (side-by-side on desktop, wrapped on mobile) */}
        {((onSearch && title) || bottomActions) && (
          <div className={`flex ${inlineBottomActions ? 'flex-col sm:flex-row sm:items-center' : 'flex-col'} gap-3 w-full`}>
            {onSearch && title && (
              <div className="relative w-full sm:max-w-xs">
                <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  onChange={(e) => onSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 transition-all"
                />
              </div>
            )}

            {bottomActions && (
              <div className="flex flex-wrap items-center gap-2.5 w-full">
                {bottomActions}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden shadow-lg">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/60">
                {selectable && (
                  <th className="px-4 py-3.5 w-12 text-center">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-indigo-600 bg-slate-900 border-slate-600 rounded focus:ring-indigo-500 cursor-pointer"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected; }}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}
                {visibleColumnsData.map((col, idx) => (
                  <th
                    key={col.key || idx}
                    className={`px-4 py-3.5 text-[11px] font-bold text-slate-400 uppercase tracking-widest whitespace-nowrap ${col.textAlign === 'right' ? 'text-right' : col.textAlign === 'center' ? 'text-center' : 'text-left'} ${col.width || ''}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="animate-pulse">
                    {selectable && <td className="px-4 py-3.5"><div className="w-4 h-4 bg-slate-800 rounded" /></td>}
                    {visibleColumnsData.map((col, cIdx) => (
                      <td key={`skel-td-${cIdx}`} className="px-4 py-3.5">
                        <div className={`h-3.5 bg-slate-800 rounded ${cIdx === 0 ? 'w-8' : 'w-3/4'}`} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnsData.length + (selectable ? 1 : 0)} className="py-16 px-4 text-center">
                    <div className="flex flex-col items-center justify-center gap-3 text-slate-500">
                      <div className="w-14 h-14 rounded-full bg-slate-800/60 flex items-center justify-center">
                        <svg className="w-7 h-7 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-slate-400">{emptyMessage}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                data.map((item, idx) => (
                  <tr
                    key={item.id || idx}
                    className={`hover:bg-slate-800/40 transition-colors ${selectable && selectedIds.includes(item.id) ? 'bg-indigo-900/10' : ''}`}
                  >
                    {selectable && (
                      <td className="px-4 py-3.5 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-indigo-600 bg-slate-900 border-slate-600 rounded focus:ring-indigo-500 cursor-pointer"
                          checked={selectedIds.includes(item.id)}
                          onChange={(e) => handleSelectOne(item.id, e.target.checked)}
                        />
                      </td>
                    )}
                    {visibleColumnsData.map((col, cIdx) => (
                      <td
                        key={col.key || cIdx}
                        className={`px-4 py-3.5 text-slate-300 font-medium whitespace-nowrap ${col.textAlign === 'right' ? 'text-right' : col.textAlign === 'center' ? 'text-center' : 'text-left'}`}
                      >
                        {col.render ? col.render(item[col.key], item, idx) : item[col.key]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ──────────────────────────────────────────────────── */}
        {totalPages > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-800 bg-slate-950/40">
            
            {/* Left: rows per page + record count */}
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <span className="hidden sm:inline font-medium">Rows:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    if (onLimitChange) onLimitChange(Number(e.target.value));
                    if (onPageChange) onPageChange(1);
                  }}
                  className="bg-slate-900 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg px-2 py-1.5 focus:outline-none focus:border-indigo-500 cursor-pointer transition-colors"
                >
                  {[10, 20, 50, 100, 500, 1000].map(val => (
                    <option key={val} value={val}>{val}</option>
                  ))}
                </select>
              </div>
              <span className="font-medium">
                <span className="text-white font-bold">{startRecord}–{endRecord}</span>
                <span className="mx-1 text-slate-600">of</span>
                <span className="text-white font-bold">{total}</span>
              </span>
            </div>

            {/* Right: page navigation */}
            {totalPages > 0 && onPageChange && (
              <div className="flex items-center gap-1">
                {/* First */}
                <button
                  disabled={page === 1}
                  onClick={() => onPageChange(1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-25 disabled:cursor-not-allowed transition-all text-xs font-bold"
                  title="First page"
                >
                  «
                </button>
                {/* Prev */}
                <button
                  disabled={page === 1}
                  onClick={() => onPageChange(page - 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                </button>

                {/* Page indicator */}
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600/20 border border-indigo-500/30 min-w-[70px] justify-center">
                  <span className="text-xs font-black text-indigo-300">{page}</span>
                  <span className="text-slate-500 text-xs">/</span>
                  <span className="text-xs text-slate-400 font-medium">{totalPages}</span>
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => onPageChange(page + 1)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                </button>
                {/* Last */}
                <button
                  disabled={page === totalPages}
                  onClick={() => onPageChange(totalPages)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-700 bg-slate-900 text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-25 disabled:cursor-not-allowed transition-all text-xs font-bold"
                  title="Last page"
                >
                  »
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StakeTable;
